/* ============================================================================
   ATHLO+ — Virtual Challenges + Strava leaderboard
   ----------------------------------------------------------------------------
   Renders multi-sport challenge cards and a club leaderboard. The leaderboard
   reads a JSON feed (data-feed) produced by a small serverless function that
   pulls the Hub Strava Club every few hours; with no feed it shows sample data.
   Mounts into [data-challenges].

   --- Wiring the live leaderboard (do this later) -------------------------
   Strava Club activity data needs a server (tokens refresh every ~6h, secrets
   can't live in the browser). Stand up a tiny Cloudflare Worker / Vercel cron:
     1. OAuth your Hub Strava Club once; store refresh_token as a secret.
     2. Every few hours: refresh the access token, GET /clubs/{id}/activities,
        tally distance per member per sport (and per active challenge).
     3. Write JSON: [{ rank, name, sport, value, unit }] to a public URL.
     4. Put that URL in the section's "Leaderboard feed URL" setting.
   The same tally can feed the quiz/Pulse Wall real numbers (see HANDOVER).
   ========================================================================== */
window.HUB_CHALLENGES = [
  { title: 'June Base Builder', sport: 'Run', period: 'Monthly', target: 'Run 100 km this month', blurb: 'Stack the easy miles. Consistency over heroics — every run counts toward the total.' },
  { title: 'Lion City Loop', sport: 'Ride', period: 'Monthly', target: 'Ride 250 km', blurb: 'Chase the distance on the roads and park connectors. Group rides count double the fun.' },
  { title: 'Pool Crusher', sport: 'Swim', period: 'Monthly', target: 'Swim 20 km', blurb: 'Lap by lap. Log your pool and open-water swims toward the monthly target.' },
  { title: 'Tri-Threat', sport: 'Multi', period: '6 weeks', target: 'Run + ride + swim = 200 km combined', blurb: 'For the triathletes and the curious — mix all three disciplines into one total.' },
  { title: 'Sunrise Club', sport: 'Run', period: 'Monthly', target: '10 runs started before 7am', blurb: 'Beat the heat and the crowds. Early birds get the cooler tempo.' },
  { title: 'Elevation Hunter', sport: 'Ride', period: 'Monthly', target: 'Climb 5,000 m of elevation', blurb: 'Singapore is flat — get creative. Bukit Timah reps, anyone?' },
  { title: 'Streak Week', sport: 'Any', period: '7 days', target: 'Move every day for a week', blurb: 'Any sport, any distance — just don’t break the chain.' },
  { title: 'Hyrox Engine', sport: 'Functional', period: 'Monthly', target: 'Log 12 functional sessions', blurb: 'Build the engine for race day. Sled pushes and wall balls welcome.' },
  { title: 'Park Connector Quest', sport: 'Run / Ride', period: 'Open', target: 'Explore 5 different PCN routes', blurb: 'Discover the island’s green network — new route every week.' }
];

(function () {
  'use strict';

  function el(t, c, h) { var n = document.createElement(t); if (c) n.className = c; if (h != null) n.innerHTML = h; return n; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  var SAMPLE = [
    { rank: 1, name: 'Rachel T.', sport: 'Run', value: 142, unit: 'km' },
    { rank: 2, name: 'Marcus L.', sport: 'Ride', value: 540, unit: 'km' },
    { rank: 3, name: 'Priya N.', sport: 'Run', value: 121, unit: 'km' },
    { rank: 4, name: 'Daniel K.', sport: 'Swim', value: 18, unit: 'km' },
    { rank: 5, name: 'Hafiz R.', sport: 'Run', value: 110, unit: 'km' }
  ];

  function Challenges(root) {
    // Prefer admin-editable metaobjects (window.HUB_CHALLENGES_MO, emitted by the
    // section when they exist — run migrate-hub-metaobjects.js); else the JS seed.
    var CHALLENGES = (window.HUB_CHALLENGES_MO && window.HUB_CHALLENGES_MO.length) ? window.HUB_CHALLENGES_MO : (window.HUB_CHALLENGES || []);
    var feed = root.getAttribute('data-feed') || '';
    var club = root.getAttribute('data-club') || '';

    // Live progress bar for any challenge that carries a date window + goal (distance/time).
    // Additive + inert: cards without start/end/goal (evergreen challenges) render unchanged.
    var CHAL_EP = root.getAttribute('data-endpoint') || 'https://vjuhcxzmtzylzorqjtsc.supabase.co/functions/v1/strava/challenge';
    function liveProgress(c, node) {
      if (!c.start || !c.end || !c.goal) return;
      var wrap = el('div', 'chal__prog', '<div class="chal__bar"><i style="width:0%"></i></div><span class="chal__pct">Loading…</span>');
      node.insertBefore(wrap, node.querySelector('.chal__cta'));
      var qs = '?start=' + encodeURIComponent(c.start) + '&end=' + encodeURIComponent(c.end) +
        '&metric=' + encodeURIComponent(c.metric || 'distance') +
        (c.sports ? '&sports=' + encodeURIComponent(c.sports) : '') +
        '&goal=' + encodeURIComponent(c.goal);
      fetch(CHAL_EP + qs).then(function (r) { return r.json(); }).then(function (d) {
        if (!d || d.ok === false) { wrap.remove(); return; }
        var goal = parseFloat(c.goal) || 0, total = d.total || 0;
        var pct = goal ? Math.min(100, Math.round((total / goal) * 100)) : (d.pct || 0);
        wrap.querySelector('i').style.width = pct + '%';
        wrap.querySelector('.chal__pct').textContent = total + (d.unit ? ' ' + d.unit : '') + ' / ' + goal + ' · ' + pct + '%';
      }).catch(function () { wrap.remove(); });
    }

    function challengeCard(c) {
      var node = el('article', 'chal');
      var cta = club
        ? '<a class="chal__cta" href="' + esc(club) + '" target="_blank" rel="noopener">Join on Strava ↗</a>'
        : '<span class="chal__cta chal__cta--soon">Strava club coming soon</span>';
      node.innerHTML =
        '<div class="chal__top"><span class="chal__sport">' + esc(c.sport) + '</span><span class="chal__period">' + esc(c.period || '') + '</span></div>' +
        '<h3 class="chal__title">' + esc(c.title) + '</h3>' +
        '<p class="chal__target">' + esc(c.target) + '</p>' +
        (c.blurb ? '<p class="chal__blurb">' + esc(c.blurb) + '</p>' : '') +
        cta;
      liveProgress(c, node);
      return node;
    }

    function renderLeaderboard(rows, live) {
      var lb = root.querySelector('[data-chal-lb]');
      if (!lb) return;
      lb.innerHTML = '';
      var head = el('div', 'lb__head');
      head.innerHTML = '<h3 class="lb__title">Club leaderboard</h3>' +
        '<span class="lb__status ' + (live ? 'is-live' : '') + '">' + (live ? '● Live via Strava' : 'Sample — goes live when the Strava club is connected') + '</span>';
      lb.appendChild(head);
      var table = el('div', 'lb__rows');
      rows.forEach(function (r) {
        var row = el('div', 'lb__row');
        row.innerHTML =
          '<span class="lb__rank">' + esc(r.rank) + '</span>' +
          '<span class="lb__name">' + esc(r.name) + '</span>' +
          '<span class="lb__sport">' + esc(r.sport) + '</span>' +
          '<span class="lb__val">' + esc(r.value) + '<small>' + esc(r.unit || 'km') + '</small></span>';
        table.appendChild(row);
      });
      lb.appendChild(table);
    }

    var grid = root.querySelector('[data-chal-grid]');
    if (grid) CHALLENGES.forEach(function (c) { grid.appendChild(challengeCard(c)); });

    if (feed) {
      fetch(feed, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function (rows) { if (rows && rows.length) renderLeaderboard(rows, true); else renderLeaderboard(SAMPLE, false); })
        .catch(function () { renderLeaderboard(SAMPLE, false); });
    } else {
      renderLeaderboard(SAMPLE, false);
    }
  }

  function init() {
    document.querySelectorAll('[data-challenges]').forEach(function (root) {
      if (root.__challenges) return; root.__challenges = true; new Challenges(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
